import { useCallback, useRef } from "react";

export type TooltipOpenChangeHandler = (isOpen: boolean, event: Event, reason?: string) => void;

/**
 * Dismiss an open tooltip when the user presses outside it. Returns the `onOpenChange` handler to
 * wire into the floating tooltip; the outside-press branch is gated behind a latest-ref of `isOpen`
 * so a stale-closure open never re-triggers. Scroll dismissal is handled separately by the
 * `ScrollDismissBoundary` wrapper around the trigger.
 */
const useTooltipDismissFromTable = (isOpen: boolean, onClose: VoidFunction): TooltipOpenChangeHandler => {
    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;

    const onOpenChange = useCallback<TooltipOpenChangeHandler>(
        (isTooltipOpen, event, reason) => {
            if (!isOpenRef.current) {
                return;
            }

            event.stopPropagation();
            event.preventDefault();

            if (reason === "outside-press" && !isTooltipOpen) {
                onClose();
            }
        },
        [onClose],
    );

    return onOpenChange;
};

export default useTooltipDismissFromTable;
